import BuzzWSClient from '~/utils/infra/buzz/ws-client'
import {
  EVENTS as BUZZ_EVENTS,
  observer as buzzObserver,
} from '~/utils/infra/buzz/types'
import { createDemandDrivenRuntime } from '~/utils/infra/common/ws/demand-driven-runtime'
import Console from '~/utils/lib/core/console'

/** @type {ReturnType<typeof createBuzzRealtime>|null} */
let mBuzzRealtime = null
const console = new Console('plugin:realtime:buzz')

/**
 * @typedef {{
 *   channel: string;
 *   count: number;
 *   releaseDemandLease: (() => Promise<void | undefined>) | null;
 *   pendingAcquire: Promise<void | undefined> | null;
 * }} ChannelDemandEntry
 */

/**
 * @typedef {{ eventName: string, payload: any }} ReplayEmitEntry
 */

export function useBuzzRealtime() {
  if (mBuzzRealtime)
    return mBuzzRealtime

  mBuzzRealtime = createBuzzRealtime()
  return mBuzzRealtime
}

function createBuzzRealtime() {
  const client = new BuzzWSClient()
  const runtime = createDemandDrivenRuntime({
    source: 'buzz',
    client,
    onAcquire(payload) {
      return client.emit('subscribe', payload)
    },
    onRelease(payload) {
      return client.emit('unsubscribe', payload)
    },
    socketObserver: buzzObserver,
    socketEvents: BUZZ_EVENTS,
  })
  /** @type {Map<string, ChannelDemandEntry>} */
  const channelDemands = new Map()
  /** @type {Map<string, ReplayEmitEntry>} */
  const reconnectReplayEmits = new Map()
  let shouldReplayChannelSubscriptionsOnOpen = false

  buzzObserver.on(BUZZ_EVENTS.ON_SOCKET_OPEN, () => {
    if (shouldReplayChannelSubscriptionsOnOpen)
      replayChannelDemandSubscriptions()

    replayReconnectSafeEmits()

    shouldReplayChannelSubscriptionsOnOpen = false
  })

  buzzObserver.on(BUZZ_EVENTS.ON_SOCKET_CLOSE, () => {
    shouldReplayChannelSubscriptionsOnOpen = channelDemands.size > 0
  })

  /** @param {string} channel */
  function acquireChannelDemand(channel) {
    const current = channelDemands.get(channel)
    if (current) {
      current.count += 1
      return
    }

    /** @type {ChannelDemandEntry} */
    const entry = {
      channel,
      count: 1,
      releaseDemandLease: null,
      pendingAcquire: null,
    }
    channelDemands.set(channel, entry)

    entry.pendingAcquire = runtime.acquireDemand({ channel })
      .then((demandLease) => {
        if (channelDemands.get(channel) !== entry) {
          return demandLease.release()
            .catch((error) => {
              console.warn('late demand release failed', error)
            })
        }

        entry.releaseDemandLease = demandLease.release
      })
      .catch((error) => {
        if (channelDemands.get(channel) === entry)
          channelDemands.delete(channel)
        console.warn('channel demand acquire failed', error)
      })
      .finally(() => {
        if (channelDemands.get(channel) === entry)
          entry.pendingAcquire = null
      })
  }

  /** @param {string} channel */
  function releaseChannelDemand(channel) {
    const entry = channelDemands.get(channel)
    if (!entry)
      return

    entry.count -= 1
    if (entry.count > 0)
      return

    channelDemands.delete(channel)

    const releaseDemand = () => entry.releaseDemandLease
      ? entry.releaseDemandLease().catch((error) => {
        console.warn('channel demand release failed', error)
      })
      : Promise.resolve()

    if (entry.pendingAcquire) {
      entry.pendingAcquire
        .then(() => releaseDemand())
        .catch(() => releaseDemand())
      return
    }

    releaseDemand()
  }

  /**
   * @param {string} eventName
   * @param {(...args: Array<any>) => void} handler
   * @returns {() => void}
   */
  function on(eventName, handler) {
    if (typeof handler !== 'function')
      return () => {}

    acquireChannelDemand(eventName)
    client.on(eventName, handler)
    return () => off(eventName, handler)
  }

  /**
   * @param {string} eventName
   * @param {(...args: Array<any>) => void} handler
   */
  function off(eventName, handler) {
    client.off(eventName, handler)
    releaseChannelDemand(eventName)
  }

  /**
   * @param {(...args: Array<any>) => void} handler
   * @returns {() => void}
   */
  function onAny(handler) {
    return client.onAny(handler)
  }

  /** @param {(...args: Array<any>) => void} handler */
  function offAny(handler) {
    return client.offAny(handler)
  }

  /**
   * @param {string} eventName
   * @param {*} payload
   * @returns {Promise<void | undefined>}
   */
  function emit(eventName, payload) {
    const replayMode = getReplayMode(eventName)
    rememberReconnectSafeEmit(eventName, payload, replayMode)

    const socket = client.getSocket()
    if (socket && socket.connected)
      return client.emit(eventName, payload)

    if (replayMode === 'leave')
      return Promise.resolve()

    return client.connect()
      .then(() => {
        return client.emit(eventName, payload)
      })
      .catch((error) => {
        console.warn('emit before connect failed', error)
      })
  }

  /**
   * @param {{ clearSubscriptions?: boolean }} [options]
   * @returns {Promise<void>}
   */
  function forceDisconnect(options) {
    channelDemands.clear()
    reconnectReplayEmits.clear()
    shouldReplayChannelSubscriptionsOnOpen = false
    return runtime.forceDisconnect(options)
  }

  /**
   * @param {string} eventName
   * @param {*} payload
   * @param {'join'|'leave'|'none'} replayMode
   */
  function rememberReconnectSafeEmit(eventName, payload, replayMode) {
    if (replayMode === 'none')
      return

    const replayKey = getReplayKey(eventName)
    if (replayMode === 'join') {
      reconnectReplayEmits.set(replayKey, { eventName, payload })
    } else {
      reconnectReplayEmits.delete(replayKey)
    }
  }

  function replayReconnectSafeEmits() {
    reconnectReplayEmits.forEach((entry) => {
      client.emit(entry.eventName, entry.payload)
        .catch((error) => {
          console.warn('replay reconnect-safe emit failed', error)
        })
    })
  }

  function replayChannelDemandSubscriptions() {
    channelDemands.forEach((entry) => {
      client.emit('subscribe', { channel: entry.channel })
        .catch((error) => {
          console.warn('replay channel subscribe failed', error)
        })
    })
  }

  return {
    forceDisconnect,
    getSnapshot: runtime.getSnapshot,
    on,
    off,
    onAny,
    offAny,
    emit,
    getProxy() {
      return client
    },
  }
}

/** @param {string} eventName */
function getReplayKey(eventName) {
  return eventName.replace(/join$/i, '').replace(/leave$/i, '')
}

/**
 * @param {string} eventName
 * @returns {'join'|'leave'|'none'}
 */
function getReplayMode(eventName) {
  if ((/(^|[:/_-])join$/i).test(eventName))
    return 'join'

  if ((/(^|[:/_-])leave$/i).test(eventName))
    return 'leave'

  return 'none'
}
